import { format } from 'date-fns';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import uuid from 'uuid/v4';

import { aiMessages } from '@atlaskit/editor-common/ai-messages';
import {
	type ExtractInjectionAPI,
	type PMPluginFactoryParams,
} from '@atlaskit/editor-common/types';
import { type EditorView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';

import type { AIPlugin } from '../../../aiPluginType';
import type { EditorPluginAIProvider } from '../../../types/types';
import type { EditorPluginAIConfigItem } from '../../../ui/prebuilt/config-items/config-items';
import type { AIExperienceMachineContextForRequest } from '../../../ui/utils/request/types';
import { createStreaming } from '../../../ui/utils/streaming';
import { type StreamingCallback } from '../types';
import { shouldReturnFallbackSuggestedTitle } from '../utils/config-utils';

import {
	fireFallbackSuggestedTitleReturnedEvent,
	fireSuggestedTitleCompletedEvent,
	isSuggestTitle,
} from './analytic-utils';

const handleFallbackSuggestedTitle = (
	streamingCallback: StreamingCallback,
	api: ExtractInjectionAPI<AIPlugin> | undefined,
	analyticSourceId: string,
	fallbackSuggestedTitle: string,
	isDateTimeFormat?: boolean,
	// eslint-disable-next-line @typescript-eslint/max-params
): void => {
	streamingCallback({
		type: 'complete',
		result: fallbackSuggestedTitle,
	});
	fireFallbackSuggestedTitleReturnedEvent({ api, analyticSourceId, isDateTimeFormat });
};

export const handleStreamingService = ({
	getIntl,
	editorPluginAIProvider,
	currentEditorView,
	configItem,
	streamingCallback,
	api,
	analyticSourceId,
}: {
	analyticSourceId: string;
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	configItem: EditorPluginAIConfigItem;
	currentEditorView: EditorView;
	editorPluginAIProvider: EditorPluginAIProvider;
	getIntl: PMPluginFactoryParams['getIntl'];
	streamingCallback: StreamingCallback;
}) => {
	const intl = getIntl();
	// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
	const uid = uuid();
	const context: AIExperienceMachineContextForRequest = {
		aiProvider: editorPluginAIProvider,
		editorView: currentEditorView,
		configItem: configItem,
		intl: intl,
		positions: [0, 0],
		isEmpty: true,
		selectionType: 'empty',
		usePageKnowledge: true,
		analyticsContext: {
			aiSessionId: uid,
			aiGroupId: uid,
		},
		responseHistory: { positionFromEnd: 0, entries: [], totalSize: 0, traversals: 0 },
	};

	streamingCallback({
		type: 'initialising',
	});

	// For proactive title generation project: We carve out a specific scenario when to
	// return a 'fallback title' if no context will be sent with the suggest a title prompt
	// (which would otherwise generate training data gibberish)
	if (shouldReturnFallbackSuggestedTitle(configItem, currentEditorView)) {
		let fallbackSuggestedTitle = intl.formatMessage(aiMessages.eventHubfallbackSuggestedTitle);
		let isDateTimeFormat;
		if (fg('platform_editor_ai_date_time_format_title_fallback')) {
			isDateTimeFormat = true;
			const fallbackTitleMessage = intl.formatMessage(
				aiMessages.eventHubfallbackSuggestedTitlePrefix,
			);
			const now = new Date();
			const dateTimeString = format(now, 'yyyy-MM-dd HH:mm');
			fallbackSuggestedTitle = `${fallbackTitleMessage} ${dateTimeString}`;
		}
		handleFallbackSuggestedTitle(
			streamingCallback,
			api,
			analyticSourceId,
			fallbackSuggestedTitle,
			isDateTimeFormat,
		);
		return;
	}

	const callbackHandler = createStreaming({
		fireAIAnalyticsEvent: () => {},
		streamFlowPacer: null,
	});
	callbackHandler(context, (streamData) => {
		if (streamData) {
			// @ts-ignore callbackHandler's type expectation is too wide, need to narrow down
			switch (streamData.type) {
				case 'stream': {
					if (streamData.loadingStatus === 'ANALYSING_QUERY') {
						streamingCallback({
							type: 'analysingQuery',
						});
					} else {
						streamingCallback({
							type: 'streaming',
							result: streamData.markdown,
						});
					}
					break;
				}
				case 'complete': {
					streamingCallback({
						type: 'complete',
						result: streamData.markdown,
					});
					if (isSuggestTitle(configItem)) {
						fireSuggestedTitleCompletedEvent({
							api,
							analyticSourceId,
							suggestedTitle: streamData.markdown,
						});
					}
					break;
				}
				case 'error':
					streamingCallback({
						type: streamData.type,
						errorInfo: streamData.errorInfo,
					});
					break;
				case 'response too similar': {
					streamingCallback({
						type: streamData.type,
					});
					break;
				}
				default: {
					break;
				}
			}
		}
	});
};
